import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const EmailManagementDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [emailStats, setEmailStats] = useState({
    totalSent: 0,
    pendingInvites: 0,
    recentResets: 0,
    deliveryRate: 0
  });

  // Check super admin access
  useEffect(() => {
    if (!authLoading && userProfile?.role !== 'super_admin') {
      navigate('/login', { replace: true });
    }
  }, [authLoading, userProfile, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Chargement...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: 'BarChart3' }
  ];

  return (
    <>
      <Helmet>
        <title>Gestion des Emails - EndoReferral</title>
      </Helmet>
      <div className="min-h-screen bg-[#0F0F12]">
        {/* Header */}
        <header className="bg-card border-b border-white/8 sticky top-0 z-40 backdrop-blur-xl bg-card/95">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl">
                  <Icon name="Mail" size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-primary">Gestion des Emails</h1>
                  <p className="text-xs text-muted">Système premium Resend</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  onClick={() => navigate('/super-admin-dashboard')}
                  className="flex items-center gap-2 px-4 py-2 text-secondary hover:text-primary hover:bg-card-hover rounded-lg transition-colors"
                >
                  <Icon name="ArrowLeft" size={18} />
                  <span className="hidden sm:inline">Retour</span>
                </Button>
                <Button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-error hover:bg-error-bg rounded-lg transition-colors"
                >
                  <Icon name="LogOut" size={18} />
                  <span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Tabs */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 mb-8">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'text-accent border-b-2 border-accent bg-accent/5' :'text-muted hover:text-secondary hover:bg-card-hover'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-card rounded-2xl shadow-xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl">
                      <Icon name="Send" size={24} className="text-blue-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{emailStats?.totalSent}</p>
                  <p className="text-sm text-muted">Emails envoyés</p>
                </div>

                <div className="bg-card rounded-2xl shadow-xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-xl">
                      <Icon name="Clock" size={24} className="text-yellow-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{emailStats?.pendingInvites}</p>
                  <p className="text-sm text-muted">Invitations en attente</p>
                </div>

                <div className="bg-card rounded-2xl shadow-xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl">
                      <Icon name="Key" size={24} className="text-purple-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{emailStats?.recentResets}</p>
                  <p className="text-sm text-muted">Réinitialisations récentes</p>
                </div>

                <div className="bg-card rounded-2xl shadow-xl border border-white/8 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-green-500/20 rounded-xl">
                      <Icon name="TrendingUp" size={24} className="text-green-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-primary mb-1">{emailStats?.deliveryRate}%</p>
                  <p className="text-sm text-muted">Taux de livraison</p>
                </div>
              </div>

              {/* System Status */}
              <div className="bg-card rounded-2xl shadow-xl border border-white/8 p-6">
                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <Icon name="Activity" size={20} className="text-accent" />
                  Statut du Système
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-card-elevated rounded-lg border border-white/8">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-secondary">Resend API</span>
                    </div>
                    <span className="text-success font-medium">Opérationnel</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-card-elevated rounded-lg border border-white/8">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-secondary">Edge Functions</span>
                    </div>
                    <span className="text-success font-medium">Opérationnel</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-card-elevated rounded-lg border border-white/8">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-secondary">Supabase Auth</span>
                    </div>
                    <span className="text-success font-medium">Opérationnel</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default EmailManagementDashboard;